import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Permission,
  REWRITE_MAX_LENGTH,
  rewriteMinLength,
  type RewriteAction,
} from '@brandschutz/shared';
import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { useCanI } from '@/lib/rbac';
import { useToast } from '@/components/ui/Toast';
import { isApiError, istKonfigurationFehlt } from '@/lib/api';
import { rewriteText } from './api';
import {
  applyAiText,
  canUndoAiText,
  emptyAiTextState,
  isUnreviewedAiText,
  undoAiText,
  type AiTextState,
} from './ai-text-state';

/**
 * KI-Aktionsleiste für EIN Freitextfeld — kompakt unter dem Eingabefeld.
 *
 * Bewusst eine schmale Zeile statt eines schwebenden Symbols im Feld: das
 * Projektformular trägt bereits Feldhilfen, Rechtshinweise, KI-Vorschläge und
 * Prüfbefunde. Ein weiterer Baustein darf hier nur so viel Platz belegen wie
 * ein Wort.
 *
 * Die Komponente wird von <Input> / <Textarea> NUR gerendert, wenn das Feld
 * per `aiContextHint` ausdrücklich zugestimmt hat. Dadurch hängen die
 * UI-Primitive nicht an Auth- und Toast-Kontext.
 */

interface ActionDef {
  key: RewriteAction | 'clear';
  label: string;
  description: string;
  icon: IconName;
}

const ACTIONS: ReadonlyArray<ActionDef> = [
  {
    key: 'generate',
    label: 'Aus Stichworten erzeugen',
    description: 'Stichworte zu einem zusammenhängenden Text ausformulieren',
    icon: 'Sparkles',
  },
  {
    key: 'rephrase',
    label: 'Umformulieren',
    description: 'Gleicher Inhalt, neue Formulierung',
    icon: 'RotateCw',
  },
  {
    key: 'compress',
    label: 'Kürzen',
    description: 'Auf das fachlich Wesentliche eindampfen',
    icon: 'Minimize2',
  },
  {
    key: 'expand',
    label: 'Erweitern',
    description: 'Ausführlicher und konkreter fassen',
    icon: 'Expand',
  },
  {
    key: 'fachsprache',
    label: 'Fachsprache',
    description: 'In formalen BayBO-Sprachgebrauch übertragen',
    icon: 'FileText',
  },
  {
    key: 'clear',
    label: 'Feld leeren',
    description: 'Inhalt löschen — über „Rückgängig" wiederherstellbar',
    icon: 'Trash2',
  },
];

function actionLabel(key: RewriteAction | 'clear'): string {
  return ACTIONS.find((a) => a.key === key)?.label ?? 'KI-Aktion';
}

export interface FieldAiActionsProps {
  /** Aktueller Feldinhalt. */
  value: string;
  /** Schreibt einen neuen Wert ins Feld (auch '' beim Leeren und beim Rückgängig). */
  onApply: (next: string) => void;
  /**
   * Kurzer, fachlicher Kontext für die KI.
   *
   * DATENMINIMIERUNG: keine Kundennamen, keine Anschriften, kein Projekttitel —
   * nur ein fester Baustein, der das Feld beschreibt.
   */
  contextHint: string;
  /**
   * Sperre des Feldes (abgeschlossenes Projekt, fremde Feldzuweisung). Wird
   * durchgereicht, damit die KI-Knöpfe genau dieselbe Sperre erben wie die
   * Eingabe selbst.
   */
  disabled?: boolean;
  /**
   * Meldet, ob gerade unveränderter KI-Text im Feld steht. Das Feld färbt sich
   * daraufhin ein — Anforderung: KI-Einträge, die noch nicht überarbeitet oder
   * freigegeben sind, müssen deutlich erkennbar sein.
   */
  onUnreviewedChange?: (unreviewed: boolean) => void;
}

export function FieldAiActions({
  value,
  onApply,
  contextHint,
  disabled,
  onUnreviewedChange,
}: FieldAiActionsProps) {
  const canUseAi = useCanI(Permission.AI_USE);
  const toast = useToast();
  const [state, setState] = useState<AiTextState>(emptyAiTextState);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState<RewriteAction | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: (action: RewriteAction) => rewriteText({ text: value, action, contextHint }),
    onSuccess: (data, action) => {
      const next = data.rewritten.trim();
      if (!next) {
        toast.info('Kein Ergebnis', 'Die KI hat keinen Text geliefert — bitte erneut versuchen.');
        return;
      }
      setState((prev) => applyAiText(prev, value, next));
      onApply(next);
      toast.success(actionLabel(action), 'Text übernommen — über „Rückgängig" wiederherstellbar.');
    },
    onError: (err: unknown) => {
      // Kein KI-Anbieter eingerichtet: Der Server formuliert den Satz, hier
      // steht er im Toast — die gestaltete Karte hat in einem Menü über einem
      // Textfeld keinen Platz. Vorher trug JEDER 503 diese Überschrift, auch
      // eine vorübergehende Störung beim Anbieter.
      const konfiguration = istKonfigurationFehlt(err);
      if (konfiguration) {
        toast.error('Funktion nicht eingerichtet', konfiguration.detail);
        return;
      }
      const message =
        isApiError(err) && err.problem.detail
          ? err.problem.detail
          : err instanceof Error
            ? err.message
            : 'Unbekannter Fehler';
      toast.error('KI-Aktion fehlgeschlagen', message);
    },
    onSettled: () => setRunning(null),
  });

  const { mutate } = mutation;

  const undo = useCallback(() => {
    const result = undoAiText(state);
    if (!result) return;
    setState(result.state);
    onApply(result.value);
  }, [state, onApply]);

  const runAction = useCallback(
    (key: RewriteAction | 'clear') => {
      setOpen(false);
      if (key === 'clear') {
        if (value.length === 0) return;
        setState((prev) => applyAiText(prev, value, ''));
        onApply('');
        return;
      }
      setRunning(key);
      mutate(key);
    },
    [mutate, onApply, value],
  );

  // Outside-Click + ESC schließen das Menü.
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Wird das Feld gesperrt, darf kein klickbares Menü darüber stehen bleiben.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const unreviewed = isUnreviewedAiText(state, value);
  useEffect(() => {
    onUnreviewedChange?.(unreviewed);
  }, [unreviewed, onUnreviewedChange]);

  if (!canUseAi || disabled) return null;

  const isPending = mutation.isPending;
  const laenge = value.trim().length;
  const zuLang = laenge > REWRITE_MAX_LENGTH;
  const undoMoeglich = canUndoAiText(state);

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      <div ref={containerRef} className="relative inline-flex">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={isPending}
          aria-label="KI-Aktionen für dieses Feld"
          aria-expanded={open}
          aria-haspopup="menu"
          title="KI-Aktionen für dieses Feld"
          className={cn(
            'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 transition-colors',
            'border-ink-300 text-ink-600 hover:bg-ink-100 hover:text-accent-700',
            'disabled:cursor-not-allowed disabled:opacity-50',
            (open || isPending) && 'border-accent-300 bg-accent-50 text-accent-700',
          )}
        >
          <Icon
            name={isPending ? 'Loader2' : 'Sparkles'}
            size={11}
            className={isPending ? 'animate-spin' : undefined}
          />
          <span>KI</span>
        </button>

        {open ? (
          <div
            role="menu"
            aria-label="KI-Aktionen"
            className="absolute left-0 top-full z-30 mt-1 w-64 overflow-hidden rounded-lg border border-ink-200 bg-paper shadow-elev-3"
          >
            <ul className="divide-y divide-ink-100">
              {ACTIONS.map((action) => {
                const gesperrt =
                  zuLang ||
                  (action.key === 'clear'
                    ? value.length === 0
                    : laenge < rewriteMinLength(action.key));
                return (
                  <li key={action.key}>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={gesperrt}
                      onClick={() => runAction(action.key)}
                      className={cn(
                        'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
                        gesperrt ? 'cursor-not-allowed opacity-40' : 'hover:bg-ink-50',
                      )}
                    >
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-ink-100 text-ink-600">
                        <Icon name={action.icon} size={14} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-ink-900">
                          {action.label}
                        </span>
                        <span className="block text-xs text-ink-500">{action.description}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {undoMoeglich ? (
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      undo();
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-ink-50"
                  >
                    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-ink-100 text-ink-600">
                      <Icon name="RotateCcw" size={14} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink-900">Rückgängig</span>
                      <span className="block text-xs text-ink-500">
                        Zurück auf den Stand vor der letzten KI-Aktion
                      </span>
                    </span>
                  </button>
                </li>
              ) : null}
            </ul>
            <p className="border-t border-ink-100 bg-ink-50 px-3 py-2 text-[11px] text-ink-500">
              {zuLang
                ? `Text ist länger als ${REWRITE_MAX_LENGTH} Zeichen — bitte kürzen.`
                : laenge < rewriteMinLength('generate')
                  ? 'Ein paar Stichworte eintippen — daraus erzeugt die KI einen Text.'
                  : 'Vorschläge der KI sind fachlich zu prüfen.'}
            </p>
          </div>
        ) : null}
      </div>

      {isPending ? (
        <span className="text-ink-500">
          {running ? `${actionLabel(running)} läuft …` : 'KI arbeitet …'}
        </span>
      ) : null}

      {unreviewed ? (
        <span
          title="Dieser Text stammt von der KI und wurde noch nicht überarbeitet oder freigegeben."
          className="inline-flex items-center gap-1 rounded-full border border-accent-200 bg-accent-100 px-2 py-0.5 font-medium text-accent-700"
        >
          <Icon name="Sparkles" size={10} />
          KI-Text · ungeprüft
        </span>
      ) : null}

      {undoMoeglich ? (
        <button
          type="button"
          onClick={undo}
          title="Feld auf den Stand vor der letzten KI-Aktion zurücksetzen."
          className="inline-flex items-center gap-1 rounded border border-ink-300 px-1.5 py-0.5 text-ink-700 transition-colors hover:bg-ink-100"
        >
          <Icon name="RotateCcw" size={10} />
          Rückgängig
        </button>
      ) : null}
    </div>
  );
}
